import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { dashboardService } from '../services/dashboard.service';
import type {
  DashboardOverviewResponse,
  DashboardStatistics,
  StatisticsPeriod,
} from '../types/dashboard.types';
import { SummaryCard } from '../components/dashboard/SummaryCard';
import { ActivityChart } from '../components/dashboard/ActivityChart';
import { ReviewBreakdownCard } from '../components/dashboard/ReviewBreakdownCard';
import { RecentActivityList } from '../components/dashboard/RecentActivityList';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { Button } from '../components/common/Button';

const BookIcon = (): React.ReactElement => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
  </svg>
);

const CheckIcon = (): React.ReactElement => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ClockIcon = (): React.ReactElement => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const FlameIcon = (): React.ReactElement => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1A3.75 3.75 0 0012 18z" />
  </svg>
);

export function DashboardPage(): React.ReactElement {
  const { user } = useAuth();
  const navigate = useNavigate();

  const displayName = user?.name || user?.email?.split('@')[0] || 'Learner';

  const [overview, setOverview] = useState<DashboardOverviewResponse | null>(null);
  const [statistics, setStatistics] = useState<DashboardStatistics | null>(null);
  const [period, setPeriod] = useState<StatisticsPeriod>('7d');

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOverviewData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [overviewData, statsData] = await Promise.all([
        dashboardService.getDashboardOverview(),
        dashboardService.getDashboardStatistics(period),
      ]);
      setOverview(overviewData);
      setStatistics(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data.');
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  const handlePeriodChange = async (newPeriod: StatisticsPeriod) => {
    setPeriod(newPeriod);
    setIsLoadingStats(true);
    try {
      const statsData = await dashboardService.getDashboardStatistics(newPeriod);
      setStatistics(statsData);
    } catch (err) {
      console.error('Failed to update period stats:', err);
    } finally {
      setIsLoadingStats(false);
    }
  };

  useEffect(() => {
    fetchOverviewData();
  }, [fetchOverviewData]);

  if (isLoading) {
    return (
      <div className="py-28 flex justify-center">
        <LoadingSpinner size="lg" label="Loading learning dashboard..." />
      </div>
    );
  }

  if (error || !overview || !statistics) {
    return (
      <div className="space-y-6 animate-slide-in">
        <ErrorMessage
          message={error || 'Failed to initialize dashboard.'}
          onRetry={fetchOverviewData}
        />
      </div>
    );
  }

  const { summary, reviewStatus, recentActivity } = overview;
  const isBrandNew = summary.totalVocabulary === 0;

  return (
    <div className="space-y-6 animate-slide-in pb-12">
      {/* ─── WELCOME & QUICK ACTIONS BANNER ────────────────────── */}
      <div className="relative overflow-hidden glass-card p-6 sm:p-8 border-primary-500/20">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600/15 via-transparent to-accent-600/10 pointer-events-none" />
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1.5">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              Welcome back, <span className="gradient-text">{displayName}</span>!
            </h1>
            <p className="text-sm text-slate-300 max-w-xl leading-relaxed">
              {isBrandNew
                ? 'Ready to build your IELTS vocabulary library? Add words to start studying with flashcards and spaced repetition.'
                : summary.dueForReview > 0
                ? `You have ${summary.dueForReview} vocabulary ${
                    summary.dueForReview === 1 ? 'word' : 'words'
                  } scheduled for review today. Keep your memory sharp!`
                : "You're all caught up on reviews for now! Great consistency with your IELTS preparations."}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 shrink-0">
            {summary.dueForReview > 0 ? (
              <Button
                id="dashboard-start-review-btn"
                variant="primary"
                onClick={() => navigate('/review')}
                className="shadow-xl shadow-primary-900/40 font-bold"
              >
                Review Due Cards ({summary.dueForReview})
              </Button>
            ) : (
              <Button
                id="dashboard-study-cards-btn"
                variant="primary"
                onClick={() => navigate('/learn')}
                className="shadow-xl shadow-primary-900/40 font-bold"
              >
                Study Flashcards
              </Button>
            )}

            <Button
              id="dashboard-add-word-btn"
              variant="secondary"
              onClick={() => navigate('/vocabulary')}
            >
              My Vocabulary
            </Button>
          </div>
        </div>
      </div>

      {/* ─── SUMMARY CARDS GRID ────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Words */}
        <SummaryCard
          id="stat-total-words"
          label="Total Words"
          value={summary.totalVocabulary}
          subtitle="Vocabulary in library"
          icon={<BookIcon />}
          variant="primary"
          onClick={() => navigate('/vocabulary')}
        />

        {/* Mastered Words */}
        <SummaryCard
          id="stat-learned-words"
          label="Mastered Words"
          value={summary.learnedVocabulary}
          subtitle={`${summary.retentionRate}% mastery retention`}
          icon={<CheckIcon />}
          variant="emerald"
          onClick={() => navigate('/learn')}
        />

        {/* Due for Review */}
        <SummaryCard
          id="stat-due-reviews"
          label="Due for Review"
          value={summary.dueForReview}
          subtitle={`${summary.unlearnedVocabulary} words to learn`}
          icon={<ClockIcon />}
          variant={summary.dueForReview > 0 ? 'rose' : 'amber'}
          onClick={() => navigate('/review')}
        />

        {/* Reviews Today */}
        <SummaryCard
          id="stat-reviews-today"
          label="Reviews Today"
          value={summary.reviewsToday}
          subtitle={`${summary.totalReviews} all-time reviews`}
          icon={<FlameIcon />}
          variant="sky"
          onClick={() => navigate('/review?tab=history')}
        />
      </div>

      {/* ─── ROW 2: CHART & REVIEW BREAKDOWN ─────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Activity Chart (7 cols on desktop) */}
        <div className="lg:col-span-7">
          <ActivityChart
            dailyActivity={statistics.dailyActivity}
            period={period}
            onPeriodChange={handlePeriodChange}
            isLoading={isLoadingStats}
          />
        </div>

        {/* Review Breakdown & Accuracy (5 cols on desktop) */}
        <div className="lg:col-span-5">
          <ReviewBreakdownCard
            breakdown={statistics.reviewBreakdown}
            accuracy={statistics.reviewAccuracy}
          />
        </div>
      </div>

      {/* ─── ROW 3: RECENT ACTIVITY & REVIEW STATUS ─────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Recent Activity List (7 cols on desktop) */}
        <div className="lg:col-span-7">
          <RecentActivityList items={recentActivity} />
        </div>

        {/* Review Deck Status Quick Widget (5 cols on desktop) */}
        <div className="lg:col-span-5 rounded-2xl p-6 border border-white/10 bg-surface-800/80 backdrop-blur-sm flex flex-col justify-between space-y-5">
          <div>
            <h3 className="text-base font-bold text-white tracking-tight">Review Deck Status</h3>
            <p className="text-xs text-slate-400 mt-0.5">Spaced repetition queue summary</p>

            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between p-3 rounded-xl bg-surface-900/60 border border-white/5">
                <span className="text-xs font-semibold text-rose-300 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-rose-400" />
                  Scheduled Due Now
                </span>
                <span className="text-sm font-bold text-white">{reviewStatus.dueCount} words</span>
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl bg-surface-900/60 border border-white/5">
                <span className="text-xs font-semibold text-amber-300 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                  Unreviewed / New
                </span>
                <span className="text-sm font-bold text-white">{reviewStatus.newCount} words</span>
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl bg-surface-900/60 border border-white/5">
                <span className="text-xs font-semibold text-emerald-300 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  Reviewed in Cycle
                </span>
                <span className="text-sm font-bold text-white">
                  {reviewStatus.reviewedCount} words
                </span>
              </div>
            </div>
          </div>

          <Button
            id="quick-review-action-btn"
            variant="primary"
            onClick={() => navigate('/review')}
            className="w-full font-bold shadow-lg shadow-primary-900/30"
          >
            Open Review Session &rarr;
          </Button>
        </div>
      </div>
    </div>
  );
}
